import { useMemo } from 'react';

import useConfiguration from 'app/hooks/useConfiguration';
import useProduct from 'app/hooks/useProduct';
import { AttractionConfig } from 'app/services/GuestCenterService.types';

export type UseAttractionResult = ReturnType<typeof useAttraction>;
export type AttractionContent = NonNullable<UseAttractionResult['attractionContent']>;

const getAttractionConfig = (
  attractionConfigs: AttractionConfig[] | undefined,
  attractionContentKey: string,
  cityCode?: string
) => {
  if (!attractionConfigs) return undefined;
  return attractionConfigs.find(
    (attr) => attr.contentKey === attractionContentKey && attr.cityCode === cityCode
  );
};

export default function useAttraction({
  productContentKey,
  attractionContentKey,
}: {
  productContentKey: string;
  attractionContentKey: string;
}) {
  const { productContent, ...useProductResult } = useProduct({ productContentKey });
  const { data: configurationResponse } = useConfiguration();
  const attractionConfig = useMemo(
    () =>
      getAttractionConfig(
        configurationResponse?.attractions.configuration,
        attractionContentKey,
        useProductResult.defaultProductLineup?.cityCode
      ),
    [
      attractionContentKey,
      configurationResponse?.attractions.configuration,
      useProductResult.defaultProductLineup?.cityCode,
    ]
  );
  const attractionContent = useMemo(() => {
    return productContent?.attractions.find((a) => a.key === attractionContentKey);
  }, [productContent?.attractions, attractionContentKey]);

  return {
    ...useProductResult,
    attractionContent,
    attractionConfig,
    productContent,
  };
}
